/**
 * @module features/client-device-management/pages/OrgDeviceManagementPage
 * @description Dedicated `{Org} Device Management` workspace opened when clicking
 * "Manage" on an active client in Client Device Management. Orchestrates assigned
 * device fleet inspection, single/batch/CSV assignment, dynamic parameter catalog
 * configuration, single-active WiFi bindings + audit logs, reusable WiFi vault,
 * and per-device-type quotas.
 */
import * as React from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertCircle,
  ArrowLeft,
  CheckSquare,
  ChevronLeft,
  ChevronRight,
  Cpu,
  Gauge,
  PlusCircle,
  RefreshCw,
  Search,
  Upload,
  Wifi,
  X,
} from "lucide-react";
import { SectionErrorBoundary } from "@/components/feedback/SectionErrorBoundary";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useClientDetail } from "@/features/clients";
import { deviceKeys, getDeviceTypes } from "@/features/devices";
import { AppError } from "@/lib/api-client";
import { AssignmentSummary, formatDeviceTypeLabel } from "../types";
import { useOrgAssignments } from "../api/assignmentApi";
import { useOrgWifiVault } from "../api/wifiApi";
import { useOrgDeviceQuotas } from "../api/quotaApi";
import { ClientLogoAvatar } from "../components/ClientLogoAvatar";
import {
  OrgAssignedDevicesList,
  OrgAssignedDevicesSkeleton,
} from "../components/OrgAssignedDevicesList";
import {
  OrgQuotasPanel,
  QuotaProgressStrip,
} from "../components/OrgQuotasPanel";
import { WifiVaultPanel } from "../components/WifiVaultPanel";
import { AssignDeviceModal } from "../components/AssignDeviceModal";
import { BatchAssignModal } from "../components/BatchAssignModal";
import { BulkCsvModal } from "../components/BulkCsvModal";
import { UnassignPromptModal } from "../components/UnassignPromptModal";
import { EditAssignedDeviceDrawer } from "../components/EditAssignedDeviceDrawer";
import { DeviceConfigDrawer } from "../components/DeviceConfigDrawer";
import { DeviceWifiDrawer } from "../components/DeviceWifiDrawer";

type WorkspaceTab = "devices" | "wifi-vault" | "quotas";

interface RouterClientSnapshot {
  id: string;
  org_name: string;
  logo_url?: string;
  city?: string;
  state?: string;
  contact_person_name?: string;
}

const PAGE_SIZE = 10;

export function OrgDeviceManagementPage() {
  const { clientId = "" } = useParams<{ clientId: string }>();
  const location = useLocation();
  const snapshot = (
    location.state as { clientSnapshot?: RouterClientSnapshot } | null
  )?.clientSnapshot;

  const [activeTab, setActiveTab] = React.useState<WorkspaceTab>("devices");
  const [page, setPage] = React.useState(1);
  const [searchInput, setSearchInput] = React.useState("");
  const deferredSearch = React.useDeferredValue(searchInput.trim());
  const [statusFilter, setStatusFilter] = React.useState("active");
  const [deviceTypeFilter, setDeviceTypeFilter] = React.useState("all");

  // Modal & Drawer state
  const [assignModalOpen, setAssignModalOpen] = React.useState(false);
  const [batchModalOpen, setBatchModalOpen] = React.useState(false);
  const [bulkCsvOpen, setBulkCsvOpen] = React.useState(false);
  const [editingAssignment, setEditingAssignment] =
    React.useState<AssignmentSummary | null>(null);
  const [configuringAssignment, setConfiguringAssignment] =
    React.useState<AssignmentSummary | null>(null);
  const [wifiAssignment, setWifiAssignment] =
    React.useState<AssignmentSummary | null>(null);
  const [unassigningAssignment, setUnassigningAssignment] =
    React.useState<AssignmentSummary | null>(null);

  const clientDetailQuery = useClientDetail(clientId);
  const deviceTypesQuery = useQuery({
    queryKey: deviceKeys.types,
    queryFn: getDeviceTypes,
    staleTime: 10 * 60_000,
  });

  const assignmentsQuery = useOrgAssignments({
    client_id: clientId,
    page,
    page_size: PAGE_SIZE,
    status: statusFilter,
    device_type: deviceTypeFilter,
    search: deferredSearch || undefined,
  });

  const quotasQuery = useOrgDeviceQuotas(clientId);
  const wifiVaultQuery = useOrgWifiVault(clientId);

  const clientDetail = clientDetailQuery.data;
  const orgName =
    clientDetail?.org_name ?? snapshot?.org_name ?? "Organization";
  const orgLogoUrl = clientDetail?.logo_url ?? snapshot?.logo_url;
  const orgCity = clientDetail?.city ?? snapshot?.city;
  const orgState = clientDetail?.state ?? snapshot?.state;
  const orgContact =
    clientDetail?.contact_person_name ?? snapshot?.contact_person_name;

  // Build extensible list of gateway types from central registry + any active quotas/assignments
  const deviceTypes = React.useMemo(() => {
    const map = new Map<string, string>();
    map.set("display_module", "Display Module");
    (deviceTypesQuery.data ?? []).forEach((dt) => map.set(dt.type_id, dt.label));
    (quotasQuery.data ?? []).forEach((q) => {
      if (!map.has(q.device_type)) {
        map.set(q.device_type, formatDeviceTypeLabel(q.device_type));
      }
    });
    (assignmentsQuery.data?.items ?? []).forEach((a) => {
      if (!map.has(a.device_type)) {
        map.set(a.device_type, formatDeviceTypeLabel(a.device_type));
      }
    });
    return Array.from(map.entries()).map(([type_id, label]) => ({
      type_id,
      label,
    }));
  }, [
    deviceTypesQuery.data,
    quotasQuery.data,
    assignmentsQuery.data?.items,
  ]);

  const handleTabSwitch = (nextTab: WorkspaceTab): void => {
    React.startTransition(() => {
      setActiveTab(nextTab);
    });
  };

  const handleRefreshAll = (): void => {
    void clientDetailQuery.refetch();
    void assignmentsQuery.refetch();
    void quotasQuery.refetch();
    void wifiVaultQuery.refetch();
  };

  // Client Not Found or Fatal Fetch Error (when no snapshot fallback exists)
  if (clientDetailQuery.isError && !snapshot) {
    const err = AppError.fromUnknown(clientDetailQuery.error);
    return (
      <div className="space-y-4">
        <Button
          variant="ghost"
          size="sm"
          asChild
          className="h-9 touch:min-h-[44px] text-xs gap-1.5"
        >
          <Link to="/s-admin/client-devices">
            <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Back to Client Device Management</span>
          </Link>
        </Button>

        <div
          role="alert"
          className="rounded-2xl border border-danger/20 bg-danger-soft p-8 text-center text-danger shadow-card"
        >
          <AlertCircle className="mx-auto h-8 w-8 text-danger" aria-hidden="true" />
          <h2 className="mt-3 font-medium text-base text-foreground">
            Unable to load organization profile
          </h2>
          <p className="mt-1 text-xs text-muted-foreground max-w-md mx-auto">
            {err.errorDetails}
          </p>
          {err.requestId && (
            <p className="mt-2 font-mono text-[11px] text-muted-foreground">
              Request ID: {err.requestId}
            </p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void clientDetailQuery.refetch()}
            className="mt-4 bg-card text-foreground text-xs"
          >
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const assignments = assignmentsQuery.data?.items ?? [];
  const pagination = assignmentsQuery.data?.pagination;
  const totalCount =
    pagination?.total_count ?? pagination?.total ?? assignments.length;
  const filtersActive =
    Boolean(searchInput.trim()) ||
    statusFilter !== "active" ||
    deviceTypeFilter !== "all";
  const assignmentsError = assignmentsQuery.error
    ? AppError.fromUnknown(assignmentsQuery.error)
    : null;

  return (
    <SectionErrorBoundary
      resetKey={clientId}
      sectionTitle={`Unable to display ${orgName} Device Management`}
    >
      <div className="space-y-4 sm:space-y-6">
        {/* Back Breadcrumb Link */}
        <div>
          <Button
            variant="ghost"
            size="sm"
            asChild
            className="-ml-2 h-9 touch:min-h-[44px] text-xs text-muted-foreground hover:text-foreground gap-1.5"
          >
            <Link to="/s-admin/client-devices">
              <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Back to Client Device Management</span>
            </Link>
          </Button>
        </div>

        {/* `{Org} Device Management` Header */}
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between pb-2">
          <div className="flex items-center gap-3.5 min-w-0">
            {clientDetailQuery.isLoading && !snapshot ? (
              <Skeleton className="h-12 w-12 rounded-2xl shrink-0" />
            ) : (
              <ClientLogoAvatar
                orgName={orgName}
                logoUrl={orgLogoUrl}
                size="lg"
              />
            )}
            <div className="min-w-0">
              {clientDetailQuery.isLoading && !snapshot ? (
                <div className="space-y-2">
                  <Skeleton className="h-7 w-64" />
                  <Skeleton className="h-3.5 w-48" />
                </div>
              ) : (
                <>
                  <h1 className="text-xl sm:text-2xl font-medium tracking-tight text-foreground truncate">
                    {orgName} Device Management
                  </h1>
                  <p className="text-xs text-muted-foreground mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="font-mono text-[11px]">{clientId}</span>
                    {orgContact && <span>· {orgContact}</span>}
                    {orgCity && orgState && (
                      <span>
                        · {orgCity}, {orgState}
                      </span>
                    )}
                  </p>
                </>
              )}
            </div>
          </div>

          {/* Fleet Action Cluster */}
          <div className="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleRefreshAll}
              disabled={assignmentsQuery.isFetching}
              className="h-10 sm:h-9 touch:min-h-[44px] text-xs gap-1.5"
              aria-label="Refresh organization devices and metrics"
            >
              <RefreshCw
                className={`h-3.5 w-3.5 ${
                  assignmentsQuery.isFetching ? "animate-spin" : ""
                }`}
                aria-hidden="true"
              />
              <span className="hidden sm:inline">Refresh</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setBulkCsvOpen(true)}
              className="h-10 sm:h-9 touch:min-h-[44px] text-xs gap-1.5"
            >
              <Upload className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Bulk CSV</span>
            </Button>

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setBatchModalOpen(true)}
              className="h-10 sm:h-9 touch:min-h-[44px] text-xs gap-1.5"
            >
              <CheckSquare className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Batch Assign</span>
            </Button>

            <Button
              type="button"
              size="sm"
              onClick={() => setAssignModalOpen(true)}
              className="h-10 sm:h-9 touch:min-h-[44px] text-xs gap-1.5"
            >
              <PlusCircle className="h-4 w-4" aria-hidden="true" />
              <span>Assign Device</span>
            </Button>
          </div>
        </div>

        {/* Segmented Workspace Tabs */}
        <div
          role="tablist"
          aria-label={`${orgName} device management sections`}
          className="inline-flex max-w-full overflow-x-auto rounded-full bg-muted p-1 gap-1"
        >
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "devices"}
            onClick={() => handleTabSwitch("devices")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-all whitespace-nowrap touch:min-h-[40px] ${
              activeTab === "devices"
                ? "bg-card text-foreground shadow-card"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Cpu className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Assigned Devices</span>
            <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] tabular-nums">
              {totalCount}
            </span>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "wifi-vault"}
            onClick={() => handleTabSwitch("wifi-vault")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-all whitespace-nowrap touch:min-h-[40px] ${
              activeTab === "wifi-vault"
                ? "bg-card text-foreground shadow-card"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Wifi className="h-3.5 w-3.5" aria-hidden="true" />
            <span>WiFi Network Vault</span>
            <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] tabular-nums">
              {(wifiVaultQuery.data ?? []).length}
            </span>
          </button>

          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "quotas"}
            onClick={() => handleTabSwitch("quotas")}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-medium transition-all whitespace-nowrap touch:min-h-[40px] ${
              activeTab === "quotas"
                ? "bg-card text-foreground shadow-card"
                : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <Gauge className="h-3.5 w-3.5" aria-hidden="true" />
            <span>Device Quotas</span>
            <span className="rounded-full bg-muted px-2 py-0.5 font-mono text-[11px] tabular-nums">
              {(quotasQuery.data ?? []).length}
            </span>
          </button>
        </div>

        {/* Tab 1: Assigned Devices Workspace */}
        {activeTab === "devices" && (
          <div className="space-y-4">
            {/* Glanceable Per-Type Quota Strip */}
            <QuotaProgressStrip
              quotas={quotasQuery.data ?? []}
              isLoading={quotasQuery.isLoading}
              deviceTypes={deviceTypes}
              onOpenQuotasTab={() => handleTabSwitch("quotas")}
            />

            {/* Search & Filters Bar */}
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2.5">
              <div className="sm:col-span-6 relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground"
                  aria-hidden="true"
                />
                <Input
                  type="search"
                  inputMode="search"
                  autoComplete="off"
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                    React.startTransition(() => setPage(1));
                  }}
                  placeholder="Search custom name, MAC, ID, or location..."
                  aria-label="Search assigned devices"
                  className="pl-9 pr-9 h-11 sm:h-10 text-base sm:text-sm rounded-xl bg-card"
                />
                {searchInput && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSearchInput("");
                      React.startTransition(() => setPage(1));
                    }}
                    aria-label="Clear device search"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 text-muted-foreground"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                )}
              </div>

              <div className="sm:col-span-3">
                <Select
                  value={deviceTypeFilter}
                  onValueChange={(val) => {
                    React.startTransition(() => {
                      setDeviceTypeFilter(val);
                      setPage(1);
                    });
                  }}
                >
                  <SelectTrigger
                    aria-label="Filter by gateway type"
                    className="h-11 sm:h-10 bg-card rounded-xl"
                  >
                    <SelectValue placeholder="All Gateway Types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Gateway Types</SelectItem>
                    {deviceTypes.map((dt) => (
                      <SelectItem key={dt.type_id} value={dt.type_id}>
                        {dt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="sm:col-span-3">
                <Select
                  value={statusFilter}
                  onValueChange={(val) => {
                    React.startTransition(() => {
                      setStatusFilter(val);
                      setPage(1);
                    });
                  }}
                >
                  <SelectTrigger
                    aria-label="Filter by assignment status"
                    className="h-11 sm:h-10 bg-card rounded-xl"
                  >
                    <SelectValue placeholder="Active Assignments" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active Assignments</SelectItem>
                    <SelectItem value="all">All History</SelectItem>
                    <SelectItem value="archived">Archived</SelectItem>
                    <SelectItem value="unassigned">Unassigned (Wiped)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* 4-State Assigned Devices View */}
            {assignmentsQuery.isLoading ? (
              <OrgAssignedDevicesSkeleton rows={5} />
            ) : assignmentsQuery.isError ? (
              <div
                role="alert"
                className="rounded-2xl border border-danger/20 bg-danger-soft p-6 sm:p-8 text-center text-danger shadow-card"
              >
                <AlertCircle
                  className="mx-auto h-8 w-8 text-danger"
                  aria-hidden="true"
                />
                <h2 className="mt-3 font-medium text-base text-foreground">
                  Failed to load assigned devices
                </h2>
                <p className="mt-1 text-xs max-w-md mx-auto text-muted-foreground">
                  {assignmentsError?.errorDetails}
                </p>
                {assignmentsError?.requestId && (
                  <p className="mt-2 font-mono text-[11px] text-muted-foreground break-all">
                    Request ID: {assignmentsError.requestId}
                  </p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void assignmentsQuery.refetch()}
                  className="mt-4 bg-card text-foreground text-xs"
                >
                  Try Again
                </Button>
              </div>
            ) : assignments.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-card/60 p-10 sm:p-12 text-center shadow-card">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-muted text-muted-foreground">
                  <Cpu className="h-6 w-6" aria-hidden="true" />
                </div>
                {filtersActive ? (
                  <>
                    <h2 className="mt-4 font-medium text-base text-foreground">
                      No devices match your filters
                    </h2>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Adjust your search query or reset filters to view active
                      assignments.
                    </p>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSearchInput("");
                        setStatusFilter("active");
                        setDeviceTypeFilter("all");
                        setPage(1);
                      }}
                      className="mt-4 text-xs touch:min-h-[44px]"
                    >
                      Reset filters
                    </Button>
                  </>
                ) : (
                  <>
                    <h2 className="mt-4 font-medium text-base text-foreground">
                      No hardware devices assigned to {orgName} yet
                    </h2>
                    <p className="mt-1 text-xs text-muted-foreground max-w-sm mx-auto">
                      Allocate unassigned units from central inventory individually,
                      in multi-select batches, or via CSV upload.
                    </p>
                    <div className="mt-4 flex flex-col sm:flex-row items-center justify-center gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => setAssignModalOpen(true)}
                        className="text-xs touch:min-h-[44px] gap-1.5"
                      >
                        <PlusCircle className="h-4 w-4" aria-hidden="true" />
                        <span>Assign First Device</span>
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setBulkCsvOpen(true)}
                        className="text-xs touch:min-h-[44px] gap-1.5"
                      >
                        <Upload className="h-4 w-4" aria-hidden="true" />
                        <span>Bulk CSV Upload</span>
                      </Button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <OrgAssignedDevicesList
                  assignments={assignments}
                  deviceTypes={deviceTypes}
                  onEdit={(item) => setEditingAssignment(item)}
                  onConfigure={(item) => setConfiguringAssignment(item)}
                  onManageWifi={(item) => setWifiAssignment(item)}
                  onUnassign={(item) => setUnassigningAssignment(item)}
                />

                {pagination && pagination.total_pages > 1 && (
                  <nav
                    aria-label="Assigned devices pagination"
                    className="flex items-center justify-between pt-2 text-xs text-muted-foreground tabular-nums"
                  >
                    <p className="font-mono">
                      {(pagination.page - 1) * pagination.page_size + 1}–
                      {Math.min(
                        pagination.page * pagination.page_size,
                        totalCount
                      )}{" "}
                      of {totalCount}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={!pagination.has_prev && pagination.page <= 1}
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        className="h-10 sm:h-8 touch:min-h-[44px] gap-1 text-xs"
                      >
                        <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
                        <span>Previous</span>
                      </Button>
                      <span className="font-mono px-1">
                        {pagination.page} / {pagination.total_pages}
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={
                          !pagination.has_next &&
                          pagination.page >= pagination.total_pages
                        }
                        onClick={() => setPage((p) => p + 1)}
                        className="h-10 sm:h-8 touch:min-h-[44px] gap-1 text-xs"
                      >
                        <span>Next</span>
                        <ChevronRight
                          className="h-3.5 w-3.5"
                          aria-hidden="true"
                        />
                      </Button>
                    </div>
                  </nav>
                )}
              </>
            )}
          </div>
        )}

        {/* Tab 2: Organization WiFi Network Vault */}
        {activeTab === "wifi-vault" && (
          <WifiVaultPanel clientId={clientId} orgName={orgName} />
        )}

        {/* Tab 3: Per-Device-Type Quotas */}
        {activeTab === "quotas" && (
          <OrgQuotasPanel
            clientId={clientId}
            orgName={orgName}
            deviceTypes={deviceTypes}
          />
        )}

        {/* Modals & Drawers */}
        <AssignDeviceModal
          open={assignModalOpen}
          onClose={() => setAssignModalOpen(false)}
          clientId={clientId}
          orgName={orgName}
          deviceTypes={deviceTypes}
        />

        <BatchAssignModal
          open={batchModalOpen}
          onClose={() => setBatchModalOpen(false)}
          clientId={clientId}
          orgName={orgName}
          deviceTypes={deviceTypes}
        />

        <BulkCsvModal
          open={bulkCsvOpen}
          onClose={() => setBulkCsvOpen(false)}
          clientId={clientId}
          orgName={orgName}
        />

        <UnassignPromptModal
          assignment={unassigningAssignment}
          open={Boolean(unassigningAssignment)}
          onClose={() => setUnassigningAssignment(null)}
        />

        <EditAssignedDeviceDrawer
          assignment={editingAssignment}
          open={Boolean(editingAssignment)}
          onClose={() => setEditingAssignment(null)}
          onSaved={(updated) => setEditingAssignment(updated)}
          deviceTypes={deviceTypes}
        />

        <DeviceConfigDrawer
          assignment={configuringAssignment}
          open={Boolean(configuringAssignment)}
          onClose={() => setConfiguringAssignment(null)}
          onSaved={(updated) => setConfiguringAssignment(updated)}
          deviceTypes={deviceTypes}
        />

        <DeviceWifiDrawer
          assignment={wifiAssignment}
          open={Boolean(wifiAssignment)}
          onClose={() => setWifiAssignment(null)}
        />
      </div>
    </SectionErrorBoundary>
  );
}

